import React, { useState, useEffect } from 'react';
import { Box, Grid, Tabs, Tab, Paper, Typography, Divider, Chip } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTransitionContext } from '~/hooks/transition/useTransitionContext';
import { useTransitionAttention } from '~/providers/TransitionAttentionProvider';
import { FlagSeverity } from '~/types/transition';
import ReferralDetailHeader from '~/components/transition/referral-detail/ReferralDetailHeader';
import ReferralIntakeCard from '~/components/transition/referral-detail/ReferralIntakeCard';
import ReferralStageActionCard from '~/components/transition/referral-detail/ReferralStageActionCard';
import DecisionFactorsCard from '~/components/transition/referral-detail/DecisionFactorsCard';
import CapacityFactorCard from '~/components/transition/referral-detail/CapacityFactorCard';
import MissingInfoCard from '~/components/transition/referral-detail/MissingInfoCard';
import DocumentsCard from '~/components/transition/referral-detail/DocumentsCard';
import ReferralTimeline from '~/components/transition/referral-detail/ReferralTimeline';
import ClinicalSummaryCard from '~/components/transition/referral-detail/ClinicalSummaryCard';
import { ReferralOperateStatusCard } from '~/components/transition/referral-detail/ReferralOperateStatusCard';
import { ReferralStatus } from '~/types/transition';

// ── Shared overview tab typography ────────────────────────────────────────────

const SECTION_LABEL = {
  textTransform: 'uppercase' as const,
  fontWeight: 600,
  letterSpacing: 0.5,
  fontSize: '0.7rem',
  color: 'grey.500',
};

const Field: React.FC<{ label: string; value?: string | null; children?: React.ReactNode }> = ({
  label, value, children,
}) => (
  <Box>
    <Typography variant="caption" sx={SECTION_LABEL}>{label}</Typography>
    {children ?? (
      <Typography variant="body1" sx={{ mt: 0.5 }}>
        {value ?? '—'}
      </Typography>
    )}
  </Box>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box>
    <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
      {title}
    </Typography>
    <Divider sx={{ mt: 1, mb: 2.5, borderBottomWidth: 2 }} />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {children}
    </Box>
  </Box>
);

// ── View ──────────────────────────────────────────────────────────────────────

const TransitionReferralDetail: React.FC = () => {
  const { referralId } = useParams<{ referralId: string }>();
  const { getById, markAsSeen } = useTransitionContext();
  const { markSeen } = useTransitionAttention();
  const [tab, setTab] = useState(0);

  const referral = referralId ? getById(referralId) : undefined;

  useEffect(() => {
    if (referralId) {
      markAsSeen(referralId);
      markSeen(referralId);
    }
  }, [referralId, markAsSeen, markSeen]);

  useEffect(() => {
    const main = document.querySelector('main');
    if (main) main.scrollTop = 0;
  }, [referralId]);

  // Reset to Overview tab when navigating to a different referral
  useEffect(() => {
    setTab(0);
  }, [referralId]);

  if (!referral) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">Referral not found.</Typography>
      </Box>
    );
  }

  const { patient } = referral;

  return (
    <Box sx={{ p: 3 }}>
      {/* Zone 1: Header */}
      <ReferralDetailHeader referral={referral} />

      {/* Zone 2: Top action cards */}
      <Grid container spacing={3} sx={{ mb: 3 }} alignItems="flex-start">
        <Grid item xs={12} md={5}>
          <ReferralIntakeCard referral={referral} />
        </Grid>
        <Grid item xs={12} md={7}>
          <ReferralStageActionCard referral={referral} />
        </Grid>
      </Grid>

      {/* Zone 2.5: Operate integration — appears after acceptance */}
      {referral.status === ReferralStatus.ACCEPTED && (
        <Box sx={{ mb: 3 }}>
          <ReferralOperateStatusCard referral={referral} />
        </Box>
      )}

      {/* Zone 3: Tabbed detail */}
      <Paper>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            '& .MuiTab-root': { fontSize: '0.95rem', fontWeight: 600, px: 3 },
          }}
        >
          <Tab label="Overview" />
          <Tab label="Clinical Summary" />
          <Tab label="Decision Criteria" />
          <Tab label="Documents" />
          <Tab label="Timeline" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* ── Tab 0: Overview ────────────────────────────────────────────── */}
          {tab === 0 && (
            <Box>
              <Grid container spacing={6} sx={{ mb: 4 }}>
                <Grid item xs={12} md={3}>
                  <Section title="Patient">
                    <Field label="Date of Birth" value={new Date(patient.dob + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} />
                    <Field label="Address">
                      <Typography variant="body1" sx={{ mt: 0.5 }}>{patient.address}</Typography>
                      <Typography variant="body1">{patient.city}, {patient.state} {patient.zip}</Typography>
                    </Field>
                    {patient.phone && <Field label="Phone" value={patient.phone} />}
                  </Section>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Section title="Insurance">
                    <Field label="Carrier" value={patient.insurance} />
                    <Field label="Member ID" value={patient.insuranceMemberId ?? '—'} />
                  </Section>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Section title="Clinical">
                    <Field label="Requested Program" value={patient.requestedProgram} />
                    <Field label="Diagnoses">
                      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {patient.diagnosisLabels.map((label, i) => (
                          <Chip
                            key={i}
                            label={`${patient.diagnosisCodes[i]} · ${label}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.72rem', height: 22 }}
                          />
                        ))}
                      </Box>
                    </Field>
                  </Section>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Section title="Referring Provider">
                    <Field label="Provider" value={patient.referringProvider} />
                    <Field label="Facility" value={patient.referringFacility} />
                    {patient.referringPhone && <Field label="Phone" value={patient.referringPhone} />}
                    {patient.referringFax && <Field label="Fax" value={patient.referringFax} />}
                  </Section>
                </Grid>
              </Grid>

              {/* AI Summary */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
                  AI Summary
                </Typography>
                <Divider sx={{ mt: 1, mb: 2, borderBottomWidth: 2 }} />
                <Typography variant="body1" sx={{ lineHeight: 1.75, color: 'text.secondary' }}>
                  {referral.summary}
                </Typography>
              </Box>

              {/* Flags */}
              {referral.flags.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
                    Flags
                  </Typography>
                  <Divider sx={{ mt: 1, mb: 2, borderBottomWidth: 2 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {referral.flags.map((flag) => (
                      <Box
                        key={flag.id}
                        sx={{
                          pl: 1.5,
                          borderLeft: '3px solid',
                          borderColor:
                            flag.severity === FlagSeverity.BLOCKING ? 'error.main' :
                            flag.severity === FlagSeverity.WARNING ? 'warning.main' : 'info.main',
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>{flag.label}</Typography>
                        {flag.detail && (
                          <Typography variant="body2" color="text.secondary">{flag.detail}</Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Missing info — read-only reference */}
              {referral.missingFields.length > 0 && (
                <MissingInfoCard referral={referral} showAction={false} />
              )}
            </Box>
          )}

          {/* ── Tab 1: Clinical Summary ─────────────────────────────────────── */}
          {tab === 1 && <ClinicalSummaryCard referral={referral} />}

          {/* ── Tab 2: Decision Criteria ────────────────────────────────────── */}
          {tab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <DecisionFactorsCard factors={referral.decisionFactors} />
              </Grid>
              <Grid item xs={12} md={4}>
                <CapacityFactorCard capacity={referral.capacityInfo} />
              </Grid>
            </Grid>
          )}

          {/* ── Tab 3: Documents ───────────────────────────────────────────── */}
          {tab === 3 && <DocumentsCard referral={referral} />}

          {/* ── Tab 4: Timeline ────────────────────────────────────────────── */}
          {tab === 4 && <ReferralTimeline events={referral.timeline} />}
        </Box>
      </Paper>
    </Box>
  );
};

export default TransitionReferralDetail;
